import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'wouter';
import type { ConfirmationResult } from 'firebase/auth';
import { api } from '@/lib/api';
import { sendOTP, confirmOTP, signInWithGoogle, resetRecaptcha, mapFirebaseError } from '@/lib/firebase';

export interface ShopOwner {
  id: string;
  shopId: string;
  shopName: string;
  shopNameAr: string;
  name: string;
  phone: string;
  role: 'owner';
}

interface AuthState {
  user: ShopOwner | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export function useAuth() {
  const [, navigate] = useLocation();
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });
  const confirmationRef = useRef<ConfirmationResult | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('shop_data');
    if (stored) {
      try {
        const user = JSON.parse(stored) as ShopOwner;
        setState({ user, isLoading: false, isAuthenticated: true });
      } catch {
        localStorage.removeItem('shop_data');
        setState({ user: null, isLoading: false, isAuthenticated: false });
      }
    } else {
      setState({ user: null, isLoading: false, isAuthenticated: false });
    }
  }, []);

  const sendOtp = useCallback(async (phone: string) => {
    // Format phone for Firebase (+964 prefix)
    const digits = phone.replace(/[^\d]/g, '');
    let formatted = phone;
    if (digits.startsWith('0')) formatted = '+964' + digits.slice(1);
    else if (digits.startsWith('964')) formatted = '+' + digits;
    else if (!phone.startsWith('+')) formatted = '+964' + digits;

    const confirmation = await sendOTP(formatted);
    confirmationRef.current = confirmation;
  }, []);

  const verifyOtp = useCallback(async (code: string, phone: string) => {
    if (!confirmationRef.current) throw new Error('No confirmation result');

    const firebaseIdToken = await confirmOTP(confirmationRef.current, code);

    const data = await api.post<{
      token: string;
      staffId: string;
      shopId: string;
      shopName: string;
      staffName: string;
    }>('/shop/auth/verify-firebase-token-owner', { firebaseIdToken });

    const user: ShopOwner = {
      id: data.staffId,
      shopId: data.shopId,
      shopName: data.shopName,
      shopNameAr: data.shopName,
      name: data.staffName,
      phone,
      role: 'owner' as const,
    };
    localStorage.setItem('shop_token', data.token);
    localStorage.setItem('shop_data', JSON.stringify(user));
    setState({ user, isLoading: false, isAuthenticated: true });
    navigate('/');
  }, [navigate]);

  const loginWithGoogle = useCallback(async () => {
    const firebaseIdToken = await signInWithGoogle();

    const data = await api.post<{
      token: string;
      staffId: string;
      shopId: string;
      shopName: string;
      staffName: string;
    }>('/shop/auth/verify-google', { firebaseIdToken });

    const user: ShopOwner = {
      id: data.staffId,
      shopId: data.shopId,
      shopName: data.shopName,
      shopNameAr: data.shopName,
      name: data.staffName,
      phone: '',
      role: 'owner' as const,
    };
    localStorage.setItem('shop_token', data.token);
    localStorage.setItem('shop_data', JSON.stringify(user));
    setState({ user, isLoading: false, isAuthenticated: true });
    navigate('/');
  }, [navigate]);

  const logout = useCallback(() => {
    localStorage.removeItem('shop_token');
    localStorage.removeItem('shop_data');
    resetRecaptcha();
    setState({ user: null, isLoading: false, isAuthenticated: false });
    navigate('/login');
  }, [navigate]);

  return { ...state, sendOtp, verifyOtp, loginWithGoogle, logout, mapFirebaseError };
}
